import '@shui/core/sh-icon';

import { Component, booleanAttribute, input, ChangeDetectionStrategy } from '@angular/core';

export type ShIconSize = 'xs' | 's' | 'm' | 'l';
export type ShIconStyle = 'auto' | 'classic' | 'redesigned';

@Component({
    selector: 'sh-icon',
    template: '<ng-content />',
    styles: [
        `
            :host([button].item-focused)
                outline: 0

            :host([button]:focus-visible)
                outline: var(--focus-outline)
                outline-offset: -0.125rem
        `
    ],
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.icon]': 'icon() ?? null',
        '[attr.size]': 'size() ?? null',
        '[attr.color]': 'color() ?? null',
        '[attr.href]': 'href() ?? null',
        '[attr.badge]': 'badge() ?? null',
        '[attr.icon-style]': 'iconStyle() ?? null',
        '[attr.aria-label]': 'ariaLabel() ?? null',
        '[attr.button]': 'button() ? "" : null',
        '[attr.toggle]': 'toggle() ? "" : null',
        '[attr.active]': 'active() ? "" : null',
        '[attr.disabled]': 'disabled() ? "" : null',
        '[attr.ix-background]': 'ixBackground() ? "" : null'
    }
})
export class ShIconComponent {
    /** Name of the SHUI icon (or custom url path). */
    public readonly icon = input<string | undefined>(undefined);

    /** Icon size. `xs` = 16 px, `s` = 24 px, `m` = 32 px (default), `l` = 48 px. */
    public readonly size = input<ShIconSize | undefined>(undefined);

    /** CSS color override. Use design-system tokens, e.g. `rgba(var(--ui-1), var(--opacity-1))`. */
    public readonly color = input<string | undefined>(undefined);

    /** Hyperlink to navigate to on click. */
    public readonly href = input<string | undefined>(undefined);

    /**
     * Badge indicator shown on the top-right corner.
     * Pass `''` (empty string) for a plain dot, or a number string such as `'25'` for a count.
     */
    public readonly badge = input<string | undefined>(undefined);

    /**
     * Per-instance override of the icon font variant.
     * `'auto'` (default) inherits from the global ShuiEnvironment setting.
     */
    public readonly iconStyle = input<ShIconStyle | undefined>(undefined);

    /** Label for screen readers. */
    public readonly ariaLabel = input<string | undefined>(undefined);

    /** Adds button behaviour and styles to the icon. */
    public readonly button = input(false, { transform: booleanAttribute });

    /** Enables toggle behaviour — icon turns on and off on click. Requires `button`. */
    public readonly toggle = input(false, { transform: booleanAttribute });

    /** Active state for toggle/button icons. */
    public readonly active = input(false, { transform: booleanAttribute });

    /** Applies disabled styles. */
    public readonly disabled = input(false, { transform: booleanAttribute });

    /** Adds a background to toggleable icon buttons during hover and pressed states. */
    public readonly ixBackground = input(false, { transform: booleanAttribute });
}
